import { Control, useFormContext, useWatch } from 'react-hook-form';
import StyledBooleanCheckbox from '../../styled/StyledBooleanCheckbox';
import StyledDropZone from '../../styled/StyledDropZone';
import StyledInput from '../../styled/StyledInput';
import StyledRadioGroup, {
  YesNoStyledRadioGroup,
} from '../../styled/StyledRadioGroup';
import HorizontalLine from '../../ui/HorizontalLine';
import { Label } from '../../ui/label';
import CategoryInsuranceList from './CategoryInsuranceList';
import CategoryOptionList from './CategoryOptionList';
import CategoryTarifList from './CategoryTarifList';
import CategoryTaxesList from './CategoryTaxesList';
import CategoryOneWayFeesList from './CategoryOneWayFeesList';
import { useApplyGlobalOptionsToCarRentalCategories } from '@/hooks/useCarRental';
import { useTranslation } from '@/i18n';
import StyledTranslatableInput from '@/components/styled/StyledTranslatableInput';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableTextarea from '@/components/styled/StyledTranslatableTextarea';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import TranslateIcon from '@/components/translation/TranslateIcon';
import { useTranslateFields } from '@/hooks/useTranslate';

interface CreateCarRentalCategoryFormProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const CreateCarRentalCategoryForm = ({
  control,
  index,
  language,
  isViewMode = false,
}: CreateCarRentalCategoryFormProps) => {
  const { t } = useTranslation();
  const displayCategoryOptions =
    useWatch({
      control,
      name: `carCategories.${index}.displayCategoryOptions`,
    }) === 'true';

  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  useApplyGlobalOptionsToCarRentalCategories({
    control: control,
    displayCategoryOptions,
    index,
    setValue,
  });

  const { translateFields, isTranslating } = useTranslateFields({
    control: control,
    setValue,
    fields: [
      `carCategories.${index}.category`,
      `carCategories.${index}.model`,
      `carCategories.${index}.description`,
      `carCategories.${index}.included`,
      `carCategories.${index}.notIncluded`,
    ],
    nestedFields: {
      [`carCategories.${index}.insurances`]: [
        'name',
        'shortDescription',
        'longDescription',
      ],
      [`carCategories.${index}.options`]: ['name', 'description'],
      [`carCategories.${index}.taxes`]: ['name'],
      [`carCategories.${index}.oneWayFees`]: ['name', 'description'],
    },
    targetLanguage: language,
  });

  return (
    <>
      <div className="flex gap-2 items-center">
        <h2 className="text-base font-medium text-black">
          {t('carRental.categoryForm.infoTitle', { index: index + 1 })}
        </h2>
        <TranslateIcon
          translateFields={() => translateFields({})}
          isTranslating={isTranslating}
          language={language}
          hidden={isViewMode}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <StyledTranslatableInput
          originalName={`carCategories.${index}.category`}
          control={control}
          name={`carCategories.${index}.category`}
          setValue={setValue}
          language={language}
          title={t('carRental.categoryForm.category')}
          placeholder={t('carRental.categoryForm.categoryPlaceholder')}
          disabled={isViewMode}
        />
        <StyledTranslatableInput
          control={control}
          name={`carCategories.${index}.model`}
          originalName={`carCategories.${index}.model`}
          setValue={setValue}
          language={language}
          title={t('carRental.categoryForm.model')}
          placeholder={t('carRental.categoryForm.modelPlaceholder')}
          disabled={isViewMode}
        />
      </div>
      <StyledTranslatableTextarea
        control={control}
        name={`carCategories.${index}.description`}
        originalName={`carCategories.${index}.description`}
        setValue={setValue}
        language={language}
        title={t('carRental.categoryForm.description')}
        placeholder={t('carRental.categoryForm.descriptionPlaceholder')}
        rows={3}
        disabled={isViewMode}
      />
      <StyledDropZone
        control={control}
        name={`carCategories.${index}.images`}
        title={t('carRental.categoryForm.images')}
        placeholder={t('carRental.categoryForm.imagesPlaceholder')}
        multiple
        disabled={isViewMode}
      />
      <div className="flex flex-col md:flex-row gap-4">
        <StyledInput
          control={control}
          name={`carCategories.${index}.maxCapacity`}
          title={t('carRental.categoryForm.maxCapacity')}
          placeholder={t('carRental.categoryForm.maxCapacityPlaceholder')}
          type="number"
          min={0}
          disabled={isViewMode}
        />
        <StyledInput
          control={control}
          name={`carCategories.${index}.maxNbLuggage`}
          title={t('carRental.categoryForm.maxNbLuggage')}
          placeholder={t('carRental.categoryForm.maxNbLuggagePlaceholder')}
          type="number"
          min={0}
          disabled={isViewMode}
        />
      </div>
      <Label className="text-gray-700">
        {t('carRental.categoryForm.gearboxType')}
      </Label>
      <StyledRadioGroup
        control={control}
        name={`carCategories.${index}.gearboxType`}
        options={[
          { label: t('carRental.categoryForm.gearboxManual'), value: 'manual' },
          {
            label: t('carRental.categoryForm.gearboxAuto'),
            value: 'automatic',
          },
        ]}
        disabled={isViewMode}
      />
      <Label className="text-gray-700">
        {t('carRental.categoryForm.airConditioning')}
      </Label>
      <YesNoStyledRadioGroup
        control={control}
        name={`carCategories.${index}.airConditioning`}
        disabled={isViewMode}
      />
      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/2">
          <StyledInput
            control={control}
            name={`carCategories.${index}.includedMileage`}
            title={t('carRental.categoryForm.includedMileage')}
            placeholder={t('carRental.categoryForm.includedMileagePlaceholder')}
            type="number"
            min={0}
            disabled={isViewMode}
          />
        </div>
        <StyledBooleanCheckbox
          className="flex flex-col md:pt-6 justify-center"
          control={control}
          name={`carCategories.${index}.isIncludedMileageUnlimited`}
          optionLabel={t('carRental.categoryForm.airConditioningUnlimited')}
          disabled={isViewMode}
        />
      </div>
      <div className="md:w-1/2">
        <StyledInput
          control={control}
          name={`carCategories.${index}.additionalCost`}
          title={t('carRental.categoryForm.additionalCost')}
          placeholder={t('carRental.categoryForm.additionalCostPlaceholder')}
          type="number"
          min={0}
          disabled={isViewMode}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <StyledInput
          control={control}
          name={`carCategories.${index}.standardDeductableAmount`}
          title={t('carRental.categoryForm.standardDeductableAmount')}
          placeholder={t(
            'carRental.categoryForm.standardDeductableAmountPlaceholder',
          )}
          type="number"
          min={0}
          disabled={isViewMode}
        />
        <StyledInput
          control={control}
          name={`carCategories.${index}.depositAmount`}
          title={t('carRental.categoryForm.depositAmount')}
          placeholder={t('carRental.categoryForm.depositAmountPlaceholder')}
          type="number"
          min={0}
          disabled={isViewMode}
        />
      </div>
      <div className="md:w-1/2">
        <StyledInput
          control={control}
          name={`carCategories.${index}.minimumAge`}
          title={t('carRental.categoryForm.minimumAge')}
          placeholder={t('carRental.categoryForm.minimumAgePlaceholder')}
          type="number"
          min={12}
          disabled={isViewMode}
        />
      </div>
      <StyledTranslatableTextarea
        control={control}
        name={`carCategories.${index}.included`}
        originalName={`carCategories.${index}.included`}
        setValue={setValue}
        language={language}
        title={t('carRental.categoryForm.included')}
        placeholder={t('carRental.categoryForm.includedPlaceholder')}
        rows={3}
        disabled={isViewMode}
      />
      <StyledTranslatableTextarea
        control={control}
        name={`carCategories.${index}.notIncluded`}
        originalName={`carCategories.${index}.notIncluded`}
        setValue={setValue}
        language={language}
        title={t('carRental.categoryForm.notIncluded')}
        placeholder={t('carRental.categoryForm.notIncludedPlaceholder')}
        rows={3}
        disabled={isViewMode}
      />
      <HorizontalLine />
      <h2 className="text-base font-medium text-black">
        {t('carRental.categoryForm.tarifsTitle', { index: index + 1 })}
      </h2>
      <CategoryTarifList
        control={control}
        index={index}
        isViewMode={isViewMode}
      />
      <HorizontalLine />
      <div className="flex flex-col gap-1.5">
        <p className="text-sm leading-6 font-medium text-gray-700">
          {t('carRental.categoryForm.specificOptionsQuestion')}
        </p>
        <YesNoStyledRadioGroup
          control={control}
          name={`carCategories.${index}.displayCategoryOptions`}
          disabled={isViewMode}
        />
      </div>
      {displayCategoryOptions && (
        <>
          <div className="flex gap-2 items-center">
            <h2 className="text-base font-medium text-black">
              {t('carRental.categoryForm.insurancesOptionsTaxesTitle', {
                index: index + 1,
              })}
            </h2>
            <TranslateIcon
              translateFields={() =>
                translateFields({
                  fieldsToTranslate: [],
                  allNestedFields: true,
                })
              }
              isTranslating={isTranslating}
              language={language}
              hidden={isViewMode}
            />
          </div>
          <CategoryInsuranceList
            control={control}
            index={index}
            language={language}
            isViewMode={isViewMode}
          />
          <CategoryOptionList
            control={control}
            index={index}
            language={language}
            isViewMode={isViewMode}
          />
          <CategoryTaxesList
            control={control}
            index={index}
            language={language}
            isViewMode={isViewMode}
          />
          <CategoryOneWayFeesList
            control={control}
            index={index}
            language={language}
            isViewMode={isViewMode}
          />
        </>
      )}
    </>
  );
};

export default CreateCarRentalCategoryForm;
